/**
 * ProductImage Component
 *
 * Product image display with reveal animations.
 * Features:
 * - Multiple entrance animation styles
 * - Shadow and border customization
 * - Floating/breathing idle animation
 * - Zoom on hover simulation
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, Img } from 'remotion';

export interface ProductImageProps {
  /** Image URL */
  src: string;
  /** Alt text */
  alt?: string;
  /** Frame when animation should start */
  startFrame?: number;
  /** Image width */
  width?: number;
  /** Image height */
  height?: number;
  /** Border radius */
  borderRadius?: number;
  /** Box shadow */
  shadow?: string;
  /** Border style */
  border?: string;
  /** Entrance animation type */
  entranceAnimation?: 'fadeScale' | 'slideUp' | 'slideDown' | 'slideLeft' | 'slideRight' | 'reveal';
  /** Enable floating animation after entrance */
  enableFloat?: boolean;
  /** Float amplitude in pixels */
  floatAmplitude?: number;
  /** Object fit */
  objectFit?: 'cover' | 'contain' | 'fill';
}

export const ProductImage: React.FC<ProductImageProps> = ({
  src,
  alt = 'Product',
  startFrame = 0,
  width = 400,
  height = 400,
  borderRadius = 20,
  shadow = '0 20px 60px rgba(0,0,0,0.3)',
  border,
  entranceAnimation = 'fadeScale',
  enableFloat = false,
  floatAmplitude = 5,
  objectFit = 'cover',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  // Entrance animation calculations
  let opacity = 1;
  let scale = 1;
  let translateX = 0;
  let translateY = 0;
  let clipPath = 'none';

  const entranceDuration = fps * 0.6;

  switch (entranceAnimation) {
    case 'fadeScale':
      opacity = interpolate(
        relativeFrame,
        [0, entranceDuration * 0.5],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      scale = spring({
        frame: relativeFrame,
        fps,
        from: 0.8,
        to: 1,
        durationInFrames: entranceDuration,
        config: { stiffness: 100, damping: 15 },
      });
      break;

    case 'slideUp':
      opacity = interpolate(
        relativeFrame,
        [0, entranceDuration * 0.3],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      translateY = interpolate(
        relativeFrame,
        [0, entranceDuration],
        [50, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      break;

    case 'slideDown':
      opacity = interpolate(
        relativeFrame,
        [0, entranceDuration * 0.3],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      translateY = interpolate(
        relativeFrame,
        [0, entranceDuration],
        [-50, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      break;

    case 'slideLeft':
      opacity = interpolate(
        relativeFrame,
        [0, entranceDuration * 0.3],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      translateX = interpolate(
        relativeFrame,
        [0, entranceDuration],
        [50, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      break;

    case 'slideRight':
      opacity = interpolate(
        relativeFrame,
        [0, entranceDuration * 0.3],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      translateX = interpolate(
        relativeFrame,
        [0, entranceDuration],
        [-50, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      break;

    case 'reveal':
      // Reveal from bottom to top
      const revealProgress = interpolate(
        relativeFrame,
        [0, entranceDuration],
        [0, 100],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      clipPath = `inset(${100 - revealProgress}% 0 0 0)`;
      opacity = 1;
      break;
  }

  // Floating animation (after entrance)
  let floatY = 0;
  if (enableFloat && relativeFrame > entranceDuration) {
    const floatFrame = relativeFrame - entranceDuration;
    floatY = Math.sin(floatFrame * 0.05) * floatAmplitude;
  }

  return (
    <div
      style={{
        width,
        height,
        opacity,
        transform: `scale(${scale}) translate(${translateX}px, ${translateY + floatY}px)`,
        clipPath,
        borderRadius,
        overflow: 'hidden',
        boxShadow: shadow,
        border,
      }}
    >
      <Img
        src={src}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          objectFit,
        }}
      />
    </div>
  );
};
